/**
 * ペルソナ別のカバレッジ。
 *
 * デモのレビューで出た指摘をそのまま持ち込んでいる。
 * - 見出しで意味を言い切る。「カバレッジ」という言葉を単体で置かない
 * - 1 画面 1 母数。数字は全部 basis の母集団のもので、別の母集団の数を並べない
 * - 未接続の注記（DataGap）は畳んで、初期表示では開かない
 *
 * ## 数えていないことを 0 % と描かない
 *
 * `overall` / `coverage` が null なのは「一度も集計していない」か
 * 「材料が無くて判定できなかった」のどちらかで、**0 件という意味ではない**。
 * どちらも `dataGap.why` に理由が入っているので、数字の代わりにそれを出す。
 *
 * ## 判定できなかったぶんを合計に混ぜない
 *
 * `total` は**判定できた**商品の件数で、`undecided` は含まない。画面でも
 * 足し合わせず、「判定できなかった N 件」として別に出す。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  JobProgressDocument,
  LoadingState,
  Meter,
  PageHeader,
  PersonaCoveragesDocument,
  RecomputePersonaCoverageDocument,
  Stat,
  Tag,
  formatJstDateTime,
  type PersonaCoverageFieldsFragment,
  type PopulationId,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  isRuleBasedJudge,
  RULE_BASED_JUDGE_NOTE,
  TERMINAL_JOB_STATES,
  UNKNOWN_JUDGE_NOTE,
} from "../api-capabilities";
import { PopulationSelect } from "./PopulationSelect";

/** 割合の描き方。null は「—」で、0 % と書き分ける */
function percent(rate: number | null | undefined): string {
  return rate == null ? "—" : `${Math.round(rate * 100)} %`;
}

function isTerminal(state: string): boolean {
  return TERMINAL_JOB_STATES.includes(state);
}

/** 1 起動につき 1 つ。再送では作り直さない */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function QuestionRow({
  entry,
}: {
  entry: PersonaCoverageFieldsFragment["byQuestion"][number];
}) {
  const question = entry.question;
  return (
    // 観点 1 件ぶんの塊。どの数字がどの観点のものかを、見た目の並びではなく
    // 読み上げの木からも辿れるようにしておく（結合確認もここを手がかりにする）
    <div
      role="group"
      aria-label={question.question}
      style={{ borderTop: "1px solid var(--vd-border)", paddingTop: 12 }}
    >
      <div className="vd-row" style={{ justifyContent: "space-between" }}>
        <div className="vd-card-title">
          {question.question}
          {question.enabled ? null : <Tag tone="neutral">数えていません</Tag>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="vd-stat-value">{percent(entry.coverage)}</div>
          <div className="vd-stat-note">
            {entry.covered == null || entry.total == null
              ? "まだ数えていません"
              : `${entry.covered.toLocaleString("ja-JP")} / ${entry.total.toLocaleString("ja-JP")} 件が答えられます`}
          </div>
        </div>
      </div>
      {question.note == null || question.note === "" ? null : (
        <p style={{ margin: "4px 0 0", color: "var(--vd-text-secondary)" }}>
          {question.note}
        </p>
      )}
      {entry.undecided == null || entry.undecided === 0 ? null : (
        <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
          判定できなかった商品が {entry.undecided.toLocaleString("ja-JP")} 件
          あります。この件数は上の割合の母数に入れていません（材料が無いだけで、
          満たしていないとは限らないため）。
        </p>
      )}
      {entry.dataGap === null ? null : (
        <Disclosure summary={`「${entry.dataGap.what}」を数えていない理由`}>
          <p style={{ margin: 0 }}>{entry.dataGap.why}</p>
          <p style={{ margin: "4px 0 0" }}>必要なもの: {entry.dataGap.needs}</p>
        </Disclosure>
      )}
    </div>
  );
}

function CoveragePanel({
  coverage,
}: {
  coverage: PersonaCoverageFieldsFragment;
}) {
  const persona = coverage.persona;
  const contexts = persona.brandContexts;
  const missing = coverage.byQuestion.filter(
    (entry) => entry.question.enabled && entry.coverage == null,
  );

  return (
    <div className="vd-stack">
      <Card
        title="この人の問いに、どれだけ答えられているか"
        extra={
          <HelpIcon
            slug="personas/coverage"
            anchor="この人の問いにどれだけ答えられているか"
          />
        }
        note={`この画面の数字はすべて「${coverage.basis.label}」を母数にしています。${coverage.basis.note}`}
      >
        <div className="vd-row" style={{ gap: 24, alignItems: "flex-end" }}>
          <Stat
            hero
            label={coverage.basis.label}
            value={percent(coverage.overall)}
            note={
              coverage.basis.denominator === null
                ? undefined
                : `判定できた延べ ${coverage.basis.denominator.toLocaleString("ja-JP")} 件`
            }
          />
          <div style={{ flex: "1 1 240px", minWidth: 200 }}>
            <Meter ratio={coverage.overall ?? null} />
          </div>
        </div>
        <p className="vd-stat-note" style={{ marginBottom: 0 }}>
          {coverage.computedAt === null
            ? "まだ一度も集計していません。"
            : `${formatJstDateTime(coverage.computedAt)} 時点の集計です。`}
        </p>
        <p className="vd-stat-note" style={{ marginBottom: 0 }}>
          {isRuleBasedJudge(coverage.judge)
            ? RULE_BASED_JUDGE_NOTE
            : UNKNOWN_JUDGE_NOTE}
        </p>
        {coverage.dataGap === null ? null : (
          <div className="vd-notice" role="status" style={{ marginTop: 12 }}>
            <p style={{ margin: 0 }}>{coverage.dataGap.why}</p>
            <p style={{ margin: "4px 0 0" }}>
              必要なもの: {coverage.dataGap.needs}
              {coverage.dataGap.external === null
                ? ""
                : `（接続先: ${coverage.dataGap.external}）`}
            </p>
          </div>
        )}
      </Card>

      <Card
        title="この人が知りたいこと（観点）ごとの充足"
        extra={<HelpIcon slug="personas/coverage" />}
        note="観点ごとの数字は 1 つのスコアに合成していません。どの問いが答えられていないかを見るためのものです。"
      >
        {coverage.byQuestion.length === 0 ? (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            このペルソナには観点がまだ登録されていません。
            <Link to={`/personas/${persona.id}`} style={{ marginLeft: 8 }}>
              観点を登録する
            </Link>
          </p>
        ) : (
          <div className="vd-stack">
            {coverage.byQuestion.map((entry) => (
              <QuestionRow key={entry.question.id} entry={entry} />
            ))}
          </div>
        )}
      </Card>

      <Card
        title="ブランドとして意識したい文脈"
        extra={<HelpIcon slug="personas/brand-contexts" />}
        note="人が書いて登録するものです。どのデータ源からも導けないので、生成も推定もしていません。"
      >
        {contexts.length === 0 ? (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            まだ登録されていません。
            <Link to={`/personas/${persona.id}`} style={{ marginLeft: 8 }}>
              文脈を登録する
            </Link>
          </p>
        ) : (
          <div className="vd-stack">
            {contexts.map((context) => (
              <div key={context.id}>
                <div className="vd-card-title">{context.label}</div>
                <p
                  style={{
                    margin: "2px 0 0",
                    color: "var(--vd-text-secondary)",
                  }}
                >
                  {context.body}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="まだ答えを用意できていない問い"
        extra={
          <HelpIcon
            slug="personas/coverage"
            anchor="まだ答えを用意できていない問い"
          />
        }
        note="ここに出ている観点は、材料が足りないか、まだ数えていないかのどちらかです。"
      >
        {missing.length === 0 ? (
          <p className="vd-stat-note" style={{ margin: 0 }}>
            数えられた観点はすべて割合が出ています。
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {missing.map((entry) => (
              <li key={entry.question.id}>
                {entry.question.question}
                {entry.dataGap === null ? null : (
                  <span className="vd-stat-note"> — {entry.dataGap.why}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function PersonaCoveragePage() {
  const [population, setPopulation] = useState<PopulationId>("DELIVERED");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const idempotencyKey = useRef<string | null>(null);

  const [result, refetch] = useQuery({
    query: PersonaCoveragesDocument,
    variables: { population },
  });
  const [recomputeState, recompute] = useMutation(
    RecomputePersonaCoverageDocument,
  );
  // 起動して返ってきた Job をそのまま信じ続けると、受付時の状態（QUEUED）から
  // 動かない。**進捗は台帳から引き直す**
  const [jobResult, refetchJob] = useQuery({
    query: JobProgressDocument,
    variables: { id: jobId ?? "" },
    pause: jobId === null,
    requestPolicy: "network-only" as const,
  });

  const enqueued = recomputeState.data?.recomputePersonaCoverage.job ?? null;
  const job = jobResult.data?.job ?? enqueued;
  const userErrors = recomputeState.data?.recomputePersonaCoverage.userErrors;
  // 終端で数字を 1 回だけ引き直す。引き直した結果でまた効果が走るので、
  // どのジョブぶんを引き直したかを覚えておく
  const settledJobId = useRef<string | null>(null);

  // 次に引くまでの秒数はサーバーが指示する。null なら終端なので止める
  useEffect(() => {
    if (jobId === null || job === null) {
      return;
    }
    if (isTerminal(job.state)) {
      if (settledJobId.current !== jobId) {
        settledJobId.current = jobId;
        refetch({ requestPolicy: "network-only" });
      }
      return;
    }
    const intervalSec = job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      refetchJob({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => clearTimeout(timer);
  }, [jobId, job, pollTick, refetch, refetchJob]);

  const start = useCallback(async () => {
    idempotencyKey.current ??= newIdempotencyKey();
    const response = await recompute({
      input: { population, idempotencyKey: idempotencyKey.current },
    });
    const started = response.data?.recomputePersonaCoverage.job ?? null;
    if (started !== null) {
      setJobId(started.id);
      idempotencyKey.current = null;
    }
  }, [population, recompute]);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const coverages = result.data.personaCoverages;
  const selected =
    coverages.find((entry) => entry.persona.id === selectedId) ??
    coverages[0] ??
    null;

  return (
    <div className="vd-page">
      <PageHeader
        title="ペルソナごとに、答えられている問いの割合"
        description="ブランドが定義したお客様像ごとに、その人が知りたいことへ答えられる商品がどれだけあるかを出しています。"
        titleExtra={<HelpIcon slug="personas/coverage" />}
      />

      <div className="vd-stack">
        <Card
          title="どの商品を母数にするか"
          note="1 つの画面では 1 つの母数だけを使います。切り替えると、下の数字はすべてその母数のものになります。"
        >
          <PopulationSelect value={population} onChange={setPopulation} />
        </Card>

        <Card
          title="集計をやり直す"
          extra={<HelpIcon slug="personas/coverage" anchor="集計をやり直す" />}
          note="規則ベースの判定なので AI を呼びません。費用はかかりません。"
        >
          <div className="vd-row" style={{ gap: 12, alignItems: "center" }}>
            <Button
              onClick={() => void start()}
              disabled={recomputeState.fetching}
            >
              いまの登録内容で数え直す
            </Button>
            {job === null ? null : (
              <span className="vd-stat-note">
                {isTerminal(job.state)
                  ? `集計が終わりました（${job.succeeded ?? 0} 件を数えました）。`
                  : `集計中です（${job.succeeded ?? 0} / ${job.total ?? 0}）。`}
              </span>
            )}
          </div>
          {job?.estimate == null ? null : (
            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              対象は延べ {job.estimate.targetCount.toLocaleString("ja-JP")} 件、
              AI の呼び出しは {job.estimate.estimatedLlmCalls} 回です。
              {job.estimate.costGap === null
                ? ""
                : ` ${job.estimate.costGap.why}`}
            </p>
          )}
          {userErrors === undefined || userErrors.length === 0 ? null : (
            <div className="vd-notice" role="alert" style={{ marginTop: 12 }}>
              {userErrors.map((error) => (
                <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                  {error.message}
                </p>
              ))}
            </div>
          )}
          {recomputeState.error === undefined ? null : (
            <div className="vd-notice" role="alert" style={{ marginTop: 12 }}>
              <p style={{ margin: 0 }}>
                集計を始められませんでした: {recomputeState.error.message}
              </p>
            </div>
          )}
        </Card>

        {coverages.length === 0 ? (
          <EmptyState
            title="ブランドが定義したペルソナがまだありません"
            description="カバレッジは、ブランドが定義したペルソナごとに数えます。"
          />
        ) : (
          <>
            <Card
              title="誰の目で見るか"
              note="ブランドが定義したペルソナだけを並べています。参考の初期値（集計から起こした人物像）は、複製してブランド定義にしてから数えます。"
            >
              <div className="vd-row" style={{ gap: 8, flexWrap: "wrap" }}>
                {coverages.map((entry) => (
                  <Button
                    key={entry.persona.id}
                    aria-pressed={entry.persona.id === selected?.persona.id}
                    variant={
                      entry.persona.id === selected?.persona.id
                        ? "primary"
                        : "text"
                    }
                    onClick={() => setSelectedId(entry.persona.id)}
                  >
                    {entry.persona.name}
                  </Button>
                ))}
              </div>
              {selected === null ? null : (
                <p
                  style={{
                    margin: "8px 0 0",
                    color: "var(--vd-text-secondary)",
                  }}
                >
                  {selected.persona.description}
                </p>
              )}
            </Card>

            {selected === null ? null : (
              <CoveragePanel
                key={`${selected.persona.id}-${population}`}
                coverage={selected}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
